import React from 'react';
import { Icon, Button, GlassRegular, IconButton, LogoUploader } from '../../../components/common';
import { generateAvatar } from '../ui-utils';
import { UserProfile } from '../../auth/types';

interface HeaderProps {
    user: UserProfile | null;
    onLogin: () => void;
    onLogout: () => void;
    onProfileClick?: () => void;
    
    showTitle: string;
    
    currentLogo?: string;
    onLogoChange: (url: string | null) => void;
    
    rightLogo?: string;
    onRightLogoChange: (url: string | null) => void;
    
    // Cloud
    cloudMode: boolean;
    syncStatus: 'synced' | 'pending' | 'error';
    isOffline?: boolean;
    reviewCount?: number;
    onReviewClick?: () => void;
    showFeedback?: boolean;
    onFeedbackClick?: () => void;

    // Mobile
    isMobile?: boolean;
    onMenuClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
    user, onLogin, onProfileClick,
    showTitle,
    currentLogo, onLogoChange,
    cloudMode, syncStatus, isOffline = false,
    reviewCount = 0, onReviewClick,
    showFeedback = false, onFeedbackClick,
    rightLogo, onRightLogoChange,
    isMobile = false, onMenuClick,
}) => {

    // ── Cloud status badge (shared between mobile and desktop layouts) ──
    const cloudBadge = cloudMode ? (() => {
        const cloud = reviewCount > 0
            ? { icon: 'rule', label: 'Review Needed', style: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200', pulse: false, tooltip: 'Preserved writes need review before they are applied to the shared project' }
            : isOffline
            ? { icon: 'cloud_off', label: 'Offline', style: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300', pulse: true, tooltip: 'Changes are saved locally and will sync when you reconnect' }
            : syncStatus === 'synced'
            ? { icon: 'cloud_done', label: 'Synced', style: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', pulse: false, tooltip: '' }
            : syncStatus === 'pending'
            ? { icon: 'sync', label: 'Syncing…', style: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', pulse: true, tooltip: '' }
            : { icon: 'cloud_off', label: 'Error', style: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', pulse: false, tooltip: '' };
        return (
            <div
                className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${cloud.style} ${cloud.pulse ? 'animate-pulse' : ''}`}
                title={cloud.tooltip}
            >
                <Icon name={cloud.icon} size="sm" />
                <span>{cloud.label}</span>
            </div>
        );
    })() : null;

    // ── Auth button / avatar (shared between layouts) ──
    const authSection = !user ? (
        <Button onClick={onLogin} variant="blue" leftIcon="login">
            Login
        </Button>
    ) : (
        <Button
            onClick={onProfileClick}
            className="h-10 min-h-0 w-10 rounded-full bg-transparent p-0 transition-transform hover:scale-105 hover:bg-transparent dark:focus:ring-offset-gray-800"
            aria-label="Account"
            size="sm"
            title="Account"
            variant="ghost"
        >
            {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: The image error handler only replaces a broken avatar source; the enclosing button owns interaction semantics. */}
            <img 
                src={user.photoURL || generateAvatar(user.displayName || user.email || 'U')}
                width={40}
                height={40}
                onError={(e) => {
                    e.currentTarget.src = generateAvatar(user.displayName || user.email || 'U');
                }}
                alt="User" 
                className="w-10 h-10 rounded-full border-2 border-gray-300 dark:border-gray-600 hover:border-primary transition-colors"
            />
        </Button>
    );

    const feedbackButton = showFeedback ? (
        <Button
            onClick={onFeedbackClick}
            variant="secondary"
            size="sm"
            leftIcon="feedback"
            aria-label="Feedback"
            title="Send beta feedback"
        >
            Feedback
        </Button>
    ) : null;

    const reviewButton = cloudMode && reviewCount > 0 ? (
        <Button
            aria-label={`${reviewCount} preserved ${reviewCount === 1 ? 'write needs' : 'writes need'} review`}
            leftIcon="rule"
            onClick={onReviewClick}
            size="sm"
            title="Review preserved writes that have not been applied to the shared project"
            variant="danger-subtle"
        >
            Review {reviewCount > 99 ? '99+' : reviewCount}
        </Button>
    ) : null;

    return (
        <GlassRegular
            as="header"
            className="app-header w-full z-10 print:hidden rounded-none! rounded-tl-[12px]!"
        >
            {/* ── MOBILE HEADER: [ ☰ ] Title [ cloud · avatar ] ── */}
            {isMobile ? (
                <div className="flex items-center justify-between px-3 py-2">
                    {/* Left: hamburger */}
                    {onMenuClick ? (
                        <IconButton
                            onClick={onMenuClick}
                            className="min-h-[44px] min-w-[44px] rounded-lg text-text-light hover:bg-gray-100 dark:text-text-dark dark:hover:bg-white/5"
                            icon="menu"
                            label="Open navigation"
                            size="lg"
                            variant="ghost"
                        />
                    ) : <div className="w-11" />}

                    {/* Center: title */}
                    <h1 className="text-base font-bold tracking-tight text-center text-text-light dark:text-text-dark truncate flex-1 mx-2">
                        {showTitle || 'Untitled Show'}
                    </h1>

                    {/* Right: cloud status + avatar */}
                    <div className="flex items-center gap-2">
                        {feedbackButton}
                        {cloudBadge}
                        {reviewButton}
                        {authSection}
                    </div>
                </div>
            ) : (
                /* ── DESKTOP / TABLET HEADER: Logo — Title & Actions — Logo ── */
                <div className="app-header__content max-w-[1600px] mx-auto px-4 py-2 lg:py-3">
                    <div className="app-header__desktop-layout flex flex-col lg:flex-row items-center lg:items-end justify-between gap-2 lg:gap-6">
                        {/* Left Logo */}
                        <div className="app-header__logo hidden sm:flex w-52 lg:justify-start justify-center">
                            <LogoUploader
                                currentLogo={currentLogo}
                                onLogoChange={onLogoChange}
                                placeholder="Show Logo"
                                readOnly
                            />
                        </div>

                        {/* Center - Title & Actions */}
                        <div className="app-header__title-actions flex flex-col items-center gap-2 flex-1 order-first lg:order-0">
                            <h1 className="app-header__title text-lg lg:text-2xl font-bold tracking-tight text-center text-text-light dark:text-text-dark wrap-break-word">
                                {showTitle || 'Untitled Show'}
                            </h1>

                            {/* Action Buttons */}
                            <div className="app-header__actions flex items-center gap-2 sm:gap-3 justify-center flex-wrap">
                                {feedbackButton}
                                {cloudBadge}
                                {reviewButton}
                                {authSection}
                            </div>
                        </div>

                        {/* Right Logo */}
                        <div className="app-header__logo hidden sm:flex w-52 lg:justify-end justify-center">
                            <LogoUploader
                                currentLogo={rightLogo}
                                onLogoChange={onRightLogoChange}
                                placeholder="Theater Logo"
                                readOnly
                            />
                        </div>
                    </div>
                </div>
            )}
        </GlassRegular>
    );
};













